import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { api, HttpError, setAuthToken, clearAuthToken } from "./api";
import { Icon, Modal, Panel, Empty, Tabs } from "./components";
import {
  AccountsPanel,
  ActivityFeed,
  AgentsPanel,
  AgentTypesPanel,
  BacklogSummary,
  BlockedPanel,
  categoryTypesCsv,
  PrerequisitesPanel,
  HealthStrip,
  KpiRow,
  SlotCards,
  SlotTable,
  TopBar,
  TWEAK_DEFAULTS,
  summarise,
  type ActivityFilter,
  type SlotAction,
  type Tweaks,
} from "./layout";
import { Landing } from "./Landing";
import { FleetGit } from "./FleetGit";
import { Login } from "./Login";
import { clampLoopSeconds, fmtAgo } from "./utils";
import type {
  AccountView,
  ActivityEntry,
  AgentRole,
  AgentView,
  AuthedUser,
  BackendConfig,
  BacklogTaskView,
  BlockedView,
  ChatMessage,
  FromRole,
  ModeInfo,
  Session,
  SlotView,
  StateResponse,
} from "./types";

// 1 minute auto-poll. Slower than feels "live" but the state on this
// dashboard mostly settles between worker heartbeats — operator can hit
// the per-panel Refresh buttons (Backlog modal, etc.) when they need a
// fresh read right now.
const POLL_INTERVAL_MS = 60000;
const POLL_INTERVAL_LABEL = "60s";
// Smaller live poll (Plan B): the live feed stays lean — "Load older" pages back
// through history on demand via the `before_id` cursor instead of fetching a big
// page every poll.
const ACTIVITY_LIMIT = 25;
// One server page when paging back through history.
const ACTIVITY_OLDER_PAGE = 50;
// Centralized model (2026-05-22): one backend (the central API) → one session,
// one token. The per-backend-host session scoping (old D4 fix) is gone — there
// are no longer multiple direct backends to disambiguate. clearSession() still
// sweeps any legacy "orch.session.<host>" keys so old tokens don't linger.
const SESSION_KEY = "orch.session";
const TWEAKS_KEY = "orch.tweaks";

// The first backend we try when the page loads. Once we have a session, we
// fetch the real backends list from /api/backends and replace this.
//
// Two deploy shapes are supported:
//   - Local dev: vite dev server on :5173, backend uvicorn on :8765 → need
//     :8765 here (the legacy :8026 port is retired — CLAUDE.md § orchestration).
//   - Production: nginx serves dashboard + proxies /api/* to backend on the
//     same origin → use the page origin, no port suffix.
//
// Detection: anything served from vite (port 5173) is dev. The Firebase-hosted
// SPA at agent-orchestrator[.staging].odum-research.com runs cross-origin from
// the backend EC2 VM at api.agent-orchestrator[.staging].odum-research.com —
// map the SPA hostname to its companion api.* host explicitly. Everything else
// is assumed nginx same-origin behind a reverse proxy (legacy Cloud Run pattern).
const BOOTSTRAP_URL = (() => {
  if (window.location.port === "5173") {
    // Default to the local dev backend (:8765 — uvicorn's canonical bind;
    // the stale :8026 default sent every fresh local login to a dead port,
    // e2e finding 2026-06-11); override with VITE_BACKEND_PORT to target a
    // separate dev instance without editing source.
    const devPort = (import.meta.env.VITE_BACKEND_PORT as string | undefined) ?? "8765";
    return `${window.location.protocol}//${window.location.hostname}:${devPort}`;
  }
  const host = window.location.hostname;
  if (host === "agent-orchestrator.odum-research.com") {
    return "https://api.agent-orchestrator.odum-research.com";
  }
  if (host === "agent-orchestrator.staging.odum-research.com") {
    return "https://api.agent-orchestrator.staging.odum-research.com";
  }
  return window.location.origin;
})();

// Centralized routing: the central API (BOOTSTRAP_URL) is reached directly;
// every other VM is reached THROUGH the central API's /api/vms/<id>/* proxy.
// So the dashboard never hits a worker VM directly (no mixed-content / CORS) —
// it appends the VM segment to the central origin and the server proxies it
// over the private VPC.
function backendBaseUrl(b: { id: string; url?: string }): string {
  if (!b.url || b.url === BOOTSTRAP_URL || b.id === "self") return BOOTSTRAP_URL;
  return `${BOOTSTRAP_URL}/api/vms/${b.id}`;
}

// ─── localStorage helpers ────────────────────────────────────────────────

function loadSession(): Session | null {
  // Single session under SESSION_KEY; also read any legacy per-host key once so
  // an operator with an old token isn't forced to re-login post-migration.
  for (const key of [SESSION_KEY, ...Object.keys(localStorage).filter((k) => k.startsWith("orch.session."))]) {
    try {
      const raw = localStorage.getItem(key);
      if (raw) return JSON.parse(raw) as Session;
    } catch {
      continue;
    }
  }
  return null;
}

function saveSession(s: Session): void {
  localStorage.setItem(SESSION_KEY, JSON.stringify(s));
}

function clearSession(): void {
  // Sign-out: remove the session key + sweep any legacy per-host keys.
  Object.keys(localStorage)
    .filter((k) => k === SESSION_KEY || k.startsWith("orch.session."))
    .forEach((k) => localStorage.removeItem(k));
}

function loadTweaks(): Tweaks {
  try {
    const raw = localStorage.getItem(TWEAKS_KEY);
    if (!raw) return TWEAK_DEFAULTS;
    return { ...TWEAK_DEFAULTS, ...(JSON.parse(raw) as Partial<Tweaks>) };
  } catch {
    return TWEAK_DEFAULTS;
  }
}

function saveTweaks(t: Tweaks): void {
  localStorage.setItem(TWEAKS_KEY, JSON.stringify(t));
}

// ─── root component ──────────────────────────────────────────────────────

/**
 * Auth shape (centralized model): the operator logs into the ONE central API;
 * the JWT it returns is stored in localStorage and attached to every outgoing
 * call as `Authorization: Bearer <jwt>` via api.setAuthToken(). The fleet shares
 * one JWT secret (from GCS), so the same token also authorizes calls proxied to
 * individual VMs via /api/vms/<id>/*. The server accepts unauthenticated
 * requests on the localhost-direct path today; once ALLOW_ANONYMOUS flips False,
 * this header is the only thing keeping the dashboard usable.
 */
export default function App() {
  // useState's lazy initializer runs SYNCHRONOUSLY during this component's first
  // render, BEFORE any children render their JSX or schedule their own effects.
  // That matters because child effects fire bottom-up after commit — so a child
  // useEffect that hits the API runs BEFORE App's own useEffect would. Setting
  // the token inside the initializer guarantees authToken is populated before
  // any Authorization-bearing call leaves the browser on a cold page load.
  // (Bug fix 2026-05-29: previously set in a useEffect, causing browser-refresh
  // to fire /api/state etc. with a null Authorization header → 401 → forced
  // logout on every refresh.)
  const [session, setSession] = useState<Session | null>(() => {
    const s = loadSession();
    setAuthToken(s?.token ?? null);
    return s;
  });

  // Belt-and-braces: still sync the module global when session changes (e.g.
  // operator logs out, then logs back in mid-session) — the click handlers
  // already do this explicitly, this keeps state coherent even if a future
  // code path mutates session without touching authToken.
  useEffect(() => {
    setAuthToken(session?.token ?? null);
  }, [session]);

  if (!session) {
    return (
      <Login
        onAuthed={(s) => {
          setAuthToken(s.token);
          saveSession(s);
          setSession(s);
        }}
        bootstrapUrl={BOOTSTRAP_URL}
      />
    );
  }
  return (
    <Router
      session={session}
      onSignOut={() => {
        clearAuthToken();
        clearSession();
        setSession(null);
      }}
    />
  );
}

/**
 * v0.7 Phase 2: light-weight path-based routing between the fleet Landing page
 * and the single-VM Dashboard.
 *   `/`         → Landing (cross-VM overview)
 *   `/vm/<id>`  → Dashboard scoped to that VM
 *   anything else → fall through to Dashboard (back-compat for direct deep links)
 *
 * Uses ``window.history.pushState`` for navigation — react-router-dom would be
 * overkill for two routes. Subscribes to ``popstate`` so the browser back button
 * works.
 */
function Router({ session, onSignOut }: { session: Session; onSignOut: () => void }) {
  const [path, setPath] = useState<string>(window.location.pathname);
  useEffect(() => {
    const onPop = () => setPath(window.location.pathname);
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = useCallback((to: string) => {
    window.history.pushState({}, "", to);
    setPath(to);
  }, []);

  if (path === "/" || path === "") {
    return (
      <Landing
        onNavigateToVm={(vmId) => navigate(`/vm/${vmId}`)}
        onNavigateToFleetGit={() => navigate("/fleet-git")}
        defaultBaseUrl={BOOTSTRAP_URL}
        authToken={session.token}
      />
    );
  }
  if (path === "/fleet-git") {
    // Fleet git-health page (fleet_git_health_orchestrator_2026_06_10.md).
    return <FleetGit baseUrl={BOOTSTRAP_URL} authToken={session.token} onBack={() => navigate("/")} />;
  }
  // /vm/<id> — for MVP we ignore the id (only 1 VM); future: pass to Dashboard
  // so it uses api-vm-<id>.<domain> as its baseUrl.
  return <Dashboard session={session} onSignOut={onSignOut} />;
}

// ─── dashboard ───────────────────────────────────────────────────────────

interface DashboardProps {
  session: Session;
  onSignOut: () => void;
}

function Dashboard({ session, onSignOut }: DashboardProps) {
  // Tweaks (settings)
  const [tweaks, setTweaks] = useState<Tweaks>(loadTweaks);
  const setTweak = useCallback(<K extends keyof Tweaks>(k: K, v: Tweaks[K]) => {
    setTweaks((prev) => {
      const next = { ...prev, [k]: v };
      saveTweaks(next);
      return next;
    });
  }, []);
  useEffect(() => {
    document.documentElement.style.setProperty("--accent", tweaks.accent);
  }, [tweaks.accent]);
  useEffect(() => {
    // Drives body[data-palette="..."] selectors in styles.css, which
    // remap the --tone-<role>-color CSS vars for every panel at once.
    document.body.dataset.palette = tweaks.palette;
  }, [tweaks.palette]);

  // Backends
  const [backends, setBackends] = useState<BackendConfig[]>([
    { id: session.backend_id || "self", label: "this", url: BOOTSTRAP_URL, default: true, status: "ok" },
  ]);
  // Start on the backend the operator actually logged into (session.backend_id).
  // If that id isn't in the loaded list (or it's the legacy "self" sentinel), the
  // backends-load effect below falls back to the `default: true` entry. This is
  // what makes logging into a NON-default backend (e.g. Harsh PC) land THERE
  // instead of jumping to the default (GCP) — which holds no token for this
  // session → 401 → bounce back to login. Operator can still switch backends via
  // the pill dropdown mid-session.
  const [activeId, setActiveId] = useState<string>(session.backend_id || "self");

  const activeBackend = backends.find((b) => b.id === activeId) ?? backends[0]!;
  // Central backend → direct; any other VM → through the central /api/vms/<id> proxy.
  const baseUrl = backendBaseUrl(activeBackend);

  // Live state
  const [state, setState] = useState<StateResponse | null>(null);
  const [accounts, setAccounts] = useState<AccountView[]>([]);
  const [agents, setAgents] = useState<AgentView[]>([]);
  const [activeRole, setActiveRole] = useState<AgentRole | null>(null);
  const [agentHistory, setAgentHistory] = useState<ChatMessage[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [spawnAgentOpen, setSpawnAgentOpen] = useState(false);
  const [pendingDeleteAgent, setPendingDeleteAgent] = useState<AgentView | null>(null);
  const [pendingEditAgent, setPendingEditAgent] = useState<AgentView | null>(null);
  const [pendingDeleteSlot, setPendingDeleteSlot] = useState<SlotView | null>(null);
  const [logViewerSlot, setLogViewerSlot] = useState<SlotView | null>(null);
  const [logViewerAgent, setLogViewerAgent] = useState<AgentView | null>(null);
  const [activity, setActivity] = useState<ActivityEntry[]>([]);
  // Activity feed (Plan B): server-driven category filter + cursor pagination.
  // `olderActivity` accumulates pages fetched via "Load older" (decoupled from
  // the live poll, which only ever replaces `activity`).
  const [activityFilter, setActivityFilter] = useState<ActivityFilter>("all");
  const [olderActivity, setOlderActivity] = useState<ActivityEntry[]>([]);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [activityHasMore, setActivityHasMore] = useState(true);
  // AgentTypesPanel (Plan B): show-finished toggle pulls retained terminal rows
  // from a SEPARATE fetch so the live roster + role chat stay clean.
  const [typesShowFinished, setTypesShowFinished] = useState(false);
  const [typesFinishedAgents, setTypesFinishedAgents] = useState<AgentView[]>([]);
  const [modeInfo, setModeInfo] = useState<ModeInfo | null>(null);
  const [connected, setConnected] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [resetTyping, setResetTyping] = useState<boolean>(false);
  const [adminBusy, setAdminBusy] = useState<"populate" | "reset" | null>(null);

  // UI state
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [pendingReassign, setPendingReassign] = useState<SlotView | null>(null);
  const [pendingMessage, setPendingMessage] = useState<SlotView | null>(null);
  const [pendingLoopInterval, setPendingLoopInterval] = useState<SlotView | null>(null);
  const [snapshotOpen, setSnapshotOpen] = useState(false);
  const [spawnOpen, setSpawnOpen] = useState(false);
  const [reportUsageId, setReportUsageId] = useState<string | null>(null);
  const [refreshingUsageId, setRefreshingUsageId] = useState<string | null>(null);
  const [backlogOpen, setBacklogOpen] = useState(false);
  const [reloadingBacklog, setReloadingBacklog] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 760);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Load mode info once at startup; doesn't change without a server restart.
  useEffect(() => {
    api
      .mode(baseUrl)
      .then(setModeInfo)
      .catch(() => setModeInfo(null));
  }, [baseUrl]);

  // Load backends list on mount then repoll each /healthz every 30 s so
  // the offline badge reflects real-time reachability, not just boot state.
  useEffect(() => {
    let cancelled = false;

    const probeAll = async (list: BackendConfig[]) => {
      return Promise.all(
        list.map(async (b) => {
          try {
            // Probe through the central proxy (or direct for the central itself)
            // so a worker VM's reachability is checked over the private VPC.
            await api.healthz(backendBaseUrl(b));
            return { ...b, status: "ok" as const, last_pinged_at: Date.now() };
          } catch {
            // Preserve last_pinged_at so the badge can show "last seen Xmin ago".
            return { ...b, status: "stale" as const };
          }
        }),
      );
    };

    api
      .backends(BOOTSTRAP_URL)
      .then(async (res) => {
        if (cancelled) return;
        // Ping each backend to determine its initial status.
        const probed = await probeAll(res.backends);
        if (cancelled) return;
        setBackends(probed);
        if (!probed.find((b) => b.id === activeId)) {
          const def = probed.find((b) => b.default) ?? probed[0];
          if (def) setActiveId(def.id);
        }
      })
      .catch((e) => {
        setError(`Could not load backends.json: ${e instanceof Error ? e.message : String(e)}`);
      });

    // D19: repoll healthz every 30 s so the offline badge stays fresh.
    const interval = setInterval(async () => {
      if (cancelled) return;
      setBackends((prev) => {
        // Kick off async probes; update state via closure.
        probeAll(prev).then((updated) => {
          if (!cancelled) setBackends(updated);
        });
        return prev; // return unchanged synchronously — real update comes from probeAll
      });
    }, 30_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Poll state + activity + accounts + agents from the active backend. The
  // activity fetch carries the selected category's `types=` CSV so the SQL
  // filter runs server-side BEFORE the limit (Plan B).
  const refresh = useCallback(async () => {
    try {
      const types = categoryTypesCsv(activityFilter);
      const [s, a, accs, ags] = await Promise.all([
        api.state(baseUrl),
        api.activity(baseUrl, { limit: ACTIVITY_LIMIT, types }),
        api.listAccounts(baseUrl),
        api.listAgents(baseUrl),
      ]);
      setState(s);
      setActivity(a);
      // A short live page means there's nothing older to page into.
      setActivityHasMore(a.length >= ACTIVITY_LIMIT);
      setAccounts(accs);
      setAgents(ags);
      setConnected(true);
      setError(null);
    } catch (e) {
      if (e instanceof HttpError && e.status === 401) {
        // Session expired — sign out and route to login.
        onSignOut();
        return;
      }
      setConnected(false);
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [baseUrl, onSignOut, activityFilter]);

  // "Load older" — fetch one page strictly before the oldest currently-shown row
  // (cursor pagination), append to olderActivity. Decoupled from the live poll.
  const onLoadOlderActivity = useCallback(
    async (beforeId: number) => {
      setLoadingOlder(true);
      try {
        const types = categoryTypesCsv(activityFilter);
        const page = await api.activity(baseUrl, { limit: ACTIVITY_OLDER_PAGE, types, before_id: beforeId });
        setOlderActivity((prev) => [...prev, ...page]);
        setActivityHasMore(page.length >= ACTIVITY_OLDER_PAGE);
      } catch {
        // transient — the main poll surfaces backend errors
      } finally {
        setLoadingOlder(false);
      }
    },
    [baseUrl, activityFilter],
  );

  // Changing the category tab invalidates the accumulated older pages.
  const onActivityFilter = useCallback((f: ActivityFilter) => {
    setActivityFilter(f);
    setOlderActivity([]);
    setActivityHasMore(true);
  }, []);

  // Merge the live page + paged-back older rows, de-duped by id (the live page
  // can overlap the first older page after new events arrive), newest-first.
  const mergedActivity = useMemo(() => {
    const seen = new Set<number>();
    const out: ActivityEntry[] = [];
    for (const e of [...activity, ...olderActivity]) {
      if (e.id != null) {
        if (seen.has(e.id)) continue;
        seen.add(e.id);
      }
      out.push(e);
    }
    return out;
  }, [activity, olderActivity]);

  // Fetch finished agents for the AgentTypesPanel only when the operator opts in;
  // when off, the panel reuses the live `agents` poll (no extra request).
  useEffect(() => {
    if (!typesShowFinished) {
      setTypesFinishedAgents([]);
      return;
    }
    let cancelled = false;
    const load = () =>
      api
        .listAgents(baseUrl, { include_finished: true })
        .then((rows) => {
          if (!cancelled) setTypesFinishedAgents(rows);
        })
        .catch(() => {
          /* main poll surfaces backend errors */
        });
    void load();
    const id = setInterval(load, POLL_INTERVAL_MS);
    return () => {
      cancelled = true;
      clearInterval(id);
    };
  }, [typesShowFinished, baseUrl]);

  const typesAgents = typesShowFinished ? typesFinishedAgents : agents;

  // Poll chat history for the active role.
  const refreshHistory = useCallback(async () => {
    if (!activeRole) return;
    setHistoryLoading(true);
    try {
      const res = await api.agentHistoryByRole(baseUrl, activeRole);
      setAgentHistory(res.messages);
    } catch {
      // ignore — main /api/state polling will surface backend errors
    } finally {
      setHistoryLoading(false);
    }
  }, [baseUrl, activeRole]);

  useEffect(() => {
    void refreshHistory();
    // 10s cadence — slow enough not to spam the backend with multiple tabs,
    // fast enough that an operator reply feels close to real-time. Send-time
    // optimistic append covers the gap for messages the operator just typed.
    const id = setInterval(refreshHistory, 10000);
    return () => clearInterval(id);
  }, [refreshHistory]);

  const onSendToRole = useCallback(
    async (role: string, text: string) => {
      // Optimistic append so the operator sees their message immediately;
      // the next refreshHistory tick reconciles with server-side ids/timestamps.
      const optimistic: ChatMessage = {
        id: -Date.now(),
        direction: "to_agent",
        from_role: "operator",
        content: text,
        created_at: new Date().toISOString(),
      };
      setAgentHistory((prev) => [...prev, optimistic]);
      try {
        await api.sendToRole(baseUrl, role, text, "operator");
      } finally {
        // refresh() updates the agents list (and pending_count badge);
        // refreshHistory() reconciles the optimistic message with the real id.
        await Promise.all([refresh(), refreshHistory()]);
      }
    },
    [baseUrl, refresh, refreshHistory],
  );

  const onPromoteAgent = useCallback(
    async (agentId: string, targetRole: AgentRole) => {
      await api.promoteAgent(baseUrl, agentId, targetRole);
      await refresh();
    },
    [baseUrl],
  );

  const onArchiveAgent = useCallback(
    async (agentId: string) => {
      await api.archiveAgent(baseUrl, agentId);
      await refresh();
    },
    [baseUrl],
  );

  const onRestoreAgent = useCallback(
    async (agentId: string) => {
      await api.restoreAgent(baseUrl, agentId);
      await refresh();
    },
    [baseUrl],
  );

  const onRequestDeleteAgent = useCallback((a: AgentView) => {
    setPendingDeleteAgent(a);
  }, []);

  const onConfirmDeleteAgent = useCallback(
    async (agentId: string) => {
      try {
        await api.deleteAgent(baseUrl, agentId);
        setPendingDeleteAgent(null);
        await refresh();
      } catch (e) {
        // Surface the failure (proxy/auth/unreachable) instead of swallowing it
        // as an unhandled rejection — otherwise the button silently "does nothing".
        // Leave the modal open so the operator can read the error and retry.
        setErrorMsg(setError)(e);
      }
    },
    [baseUrl, refresh],
  );

  const onRequestEditAgent = useCallback((a: AgentView) => {
    setPendingEditAgent(a);
  }, []);

  const onConfirmEditAgent = useCallback(
    async (agentId: string, fields: { label?: string; rc_url?: string; machine?: string; model?: string }) => {
      await api.updateAgent(baseUrl, agentId, fields);
      setPendingEditAgent(null);
      await refresh();
    },
    [baseUrl],
  );

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [refresh]);

  const summary = useMemo(() => (state ? summarise(state) : null), [state]);

  // ── operator actions ──

  const onAct = useCallback(
    (kind: SlotAction, slot: SlotView) => {
      if (kind === "reassign") setPendingReassign(slot);
      else if (kind === "message") setPendingMessage(slot);
      else if (kind === "loop-interval") setPendingLoopInterval(slot);
      else if (kind === "delete") setPendingDeleteSlot(slot);
      else if (kind === "log") setLogViewerSlot(slot);
      else if (kind === "pause") {
        api.pause(baseUrl, slot.slot_id).then(refresh).catch(setErrorMsg(setError));
      } else if (kind === "resume") {
        api.resume(baseUrl, slot.slot_id).then(refresh).catch(setErrorMsg(setError));
      }
    },
    [baseUrl, refresh],
  );

  const confirmReassign = useCallback(() => {
    if (!pendingReassign) return;
    api
      .reassign(baseUrl, pendingReassign.slot_id)
      .then(() => {
        setPendingReassign(null);
        refresh();
      })
      .catch(setErrorMsg(setError));
  }, [baseUrl, pendingReassign, refresh]);

  const onConfirmDeleteSlot = useCallback(
    async (slotId: number) => {
      try {
        await api.deleteSlot(baseUrl, slotId);
        setPendingDeleteSlot(null);
        await refresh();
      } catch (e) {
        // Surface the failure instead of swallowing it (see onConfirmDeleteAgent).
        setErrorMsg(setError)(e);
      }
    },
    [baseUrl, refresh],
  );

  const onAnswerBlocked = useCallback(
    (q: BlockedView, answer: string, fromRole: FromRole) => {
      setState((prev) =>
        prev ? { ...prev, blocked_queue: prev.blocked_queue.filter((b) => b.blocked_id !== q.blocked_id) } : prev,
      );
      api
        .answerBlocked(baseUrl, q.blocked_id, answer, fromRole)
        .then(refresh)
        .catch((err) => {
          refresh();
          setErrorMsg(setError)(err);
        });
    },
    [baseUrl, refresh],
  );

  const onTogglePrerequisite = useCallback(
    (name: string, value: boolean) => {
      api
        .setPrerequisite(baseUrl, name, value, session.user.role === "viewer" ? "viewer" : "operator")
        .then(refresh)
        .catch(setErrorMsg(setError));
    },
    [baseUrl, refresh, session.user.role],
  );

  const onSendMessage = useCallback(
    (slot: SlotView, text: string) => {
      api
        .sendMessage(baseUrl, slot.slot_id, text, "operator")
        .then(() => {
          setPendingMessage(null);
          refresh();
        })
        .catch(setErrorMsg(setError));
    },
    [baseUrl, refresh],
  );

  const onSetLoopInterval = useCallback(
    (slot: SlotView, seconds: number) => {
      api
        .setLoopInterval(baseUrl, slot.slot_id, clampLoopSeconds(seconds))
        .then(() => {
          setPendingLoopInterval(null);
          refresh();
        })
        .catch(setErrorMsg(setError));
    },
    [baseUrl, refresh],
  );

  const onSnapshot = useCallback(() => setSnapshotOpen(true), []);
  const confirmSnapshot = useCallback(() => {
    api
      .snapshot(baseUrl, "manual")
      .then(() => {
        setSnapshotOpen(false);
        refresh();
      })
      .catch(setErrorMsg(setError));
  }, [baseUrl, refresh]);

  const onClearRateLimit = useCallback(
    (accountId: string) => {
      api.clearRateLimit(baseUrl, accountId).then(refresh).catch(setErrorMsg(setError));
    },
    [baseUrl, refresh],
  );

  // Operator edited backlog.yaml on disk → tell the server to re-read it.
  // Auto-polling won't trigger this; server caches the parsed YAML in memory.
  const onReloadBacklog = useCallback(async () => {
    setReloadingBacklog(true);
    try {
      await api.reloadBacklog(baseUrl);
      refresh();
    } catch (e) {
      setErrorMsg(setError)(e);
    } finally {
      setReloadingBacklog(false);
    }
  }, [baseUrl, refresh]);

  const onReportUsage = useCallback((accountId: string) => {
    setReportUsageId(accountId);
  }, []);

  // Slow (~12-15s): backend spawns `claude` in a pty, sends /usage,
  // parses, updates the account row. Spinner shows on the button.
  const onRefreshUsage = useCallback(
    async (accountId: string) => {
      setRefreshingUsageId(accountId);
      try {
        await api.refreshAccountUsage(baseUrl, accountId);
        refresh();
      } catch (e) {
        setErrorMsg(setError)(e);
      } finally {
        setRefreshingUsageId(null);
      }
    },
    [baseUrl, refresh],
  );

  const onSubmitUsage = useCallback(
    async (accountId: string, weekly: number | null, fiveHour: number | null) => {
      await api.reportUsage(baseUrl, accountId, {
        weekly_msgs_used: weekly ?? undefined,
        five_hour_msgs_used: fiveHour ?? undefined,
        source: "operator",
      });
      setReportUsageId(null);
      refresh();
    },
    [baseUrl, refresh],
  );

  const handleSignOut = useCallback(() => {
    api
      .logout(baseUrl)
      .catch(() => {})
      .finally(onSignOut);
  }, [baseUrl, onSignOut]);

  const handleAdminPopulate = useCallback(() => {
    if (!confirm("Run the demo-populate script? Seeds 7 slots, 2 blocked questions, account usage.")) return;
    setAdminBusy("populate");
    api
      .adminPopulate(baseUrl)
      .then(() => refresh())
      .catch(setErrorMsg(setError))
      .finally(() => setAdminBusy(null));
  }, [baseUrl, refresh]);

  const handleAdminReset = useCallback(() => {
    setResetTyping(true);
  }, []);

  const confirmAdminReset = useCallback(
    async (confirmText: string) => {
      if (confirmText !== "reset") return;
      setAdminBusy("reset");
      try {
        await api.adminReset(baseUrl);
        await refresh();
      } catch (e) {
        setErrorMsg(setError)(e);
      } finally {
        setAdminBusy(null);
        setResetTyping(false);
      }
    },
    [baseUrl, refresh],
  );

  // ── render ──

  const sessionStartedShort = new Date(session.started_at).toISOString().slice(11, 16) + " UTC";

  return (
    <div className="app">
      <TopBar
        backends={backends}
        backendId={activeId}
        onBackend={setActiveId}
        state={state}
        onSnapshot={onSnapshot}
        connected={connected}
        mode={modeInfo?.mode ?? null}
        t={tweaks}
        setTweak={setTweak}
        user={session.user}
        sessionStartedShort={sessionStartedShort}
        onSignOut={handleSignOut}
      />
      <main>
        {modeInfo?.admin_enabled && (
          <div className="admin-strip">
            <span className="lab">mock mode</span>
            <span className="sub">
              State lives in <span className="mono">{modeInfo.db_path.split("/").pop()}</span>. Restart with{" "}
              <span className="mono">ORCHESTRATOR_MODE=live</span> for real data.
            </span>
            <button
              className="btn btn-sm"
              onClick={handleAdminPopulate}
              disabled={adminBusy !== null}
              title="Seed mock slots + blocked Qs + account usage"
            >
              {adminBusy === "populate" ? "Populating…" : "Populate demo"}
            </button>
            <button
              className="btn btn-sm btn-danger"
              onClick={handleAdminReset}
              disabled={adminBusy !== null}
              title="Drop all tables + reload backlog + accounts"
            >
              Reset state
            </button>
          </div>
        )}

        {error && !connected && state && <ErrorBanner backend={activeBackend} message={error} onRetry={refresh} />}

        {state === null ? (
          <LoadingState />
        ) : isMobile ? (
          <MobileTriage
            state={state}
            summary={summary!}
            activity={mergedActivity}
            activityFilter={activityFilter}
            onActivityFilter={onActivityFilter}
            onLoadOlderActivity={onLoadOlderActivity}
            loadingOlderActivity={loadingOlder}
            activityHasMore={activityHasMore}
            typesAgents={typesAgents}
            typesShowFinished={typesShowFinished}
            setTypesShowFinished={setTypesShowFinished}
            accounts={accounts}
            onAnswer={onAnswerBlocked}
            onAct={onAct}
            onTogglePrerequisite={onTogglePrerequisite}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            refreshingUsageId={refreshingUsageId}
          />
        ) : (
          <DesktopLayout
            t={tweaks}
            state={state}
            summary={summary!}
            activity={mergedActivity}
            activityFilter={activityFilter}
            onActivityFilter={onActivityFilter}
            onLoadOlderActivity={onLoadOlderActivity}
            loadingOlderActivity={loadingOlder}
            activityHasMore={activityHasMore}
            typesAgents={typesAgents}
            typesShowFinished={typesShowFinished}
            setTypesShowFinished={setTypesShowFinished}
            accounts={accounts}
            agents={agents}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            agentHistory={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromoteAgent={onPromoteAgent}
            onArchiveAgent={onArchiveAgent}
            onRestoreAgent={onRestoreAgent}
            onRequestDeleteAgent={onRequestDeleteAgent}
            onRequestEditAgent={onRequestEditAgent}
            onShowAgentLog={(a) => setLogViewerAgent(a)}
            onSpawnAgent={() => setSpawnAgentOpen(true)}
            expandedId={expandedId}
            setExpandedId={setExpandedId}
            onAct={onAct}
            onAnswer={onAnswerBlocked}
            onTogglePrerequisite={onTogglePrerequisite}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            refreshingUsageId={refreshingUsageId}
            onSpawn={() => setSpawnOpen(true)}
            onOpenBacklog={() => setBacklogOpen(true)}
            onReloadBacklog={onReloadBacklog}
            reloadingBacklog={reloadingBacklog}
            setTweak={setTweak}
          />
        )}
      </main>

      <ReassignModal slot={pendingReassign} onCancel={() => setPendingReassign(null)} onConfirm={confirmReassign} />
      <MessageModal slot={pendingMessage} onCancel={() => setPendingMessage(null)} onSend={onSendMessage} />
      <LoopIntervalModal
        slot={pendingLoopInterval}
        onCancel={() => setPendingLoopInterval(null)}
        onSet={onSetLoopInterval}
      />
      <SnapshotModal open={snapshotOpen} onCancel={() => setSnapshotOpen(false)} onConfirm={confirmSnapshot} />
      <SpawnModal
        open={spawnOpen}
        onCancel={() => setSpawnOpen(false)}
        slots={state?.slots ?? []}
        accounts={accounts}
        backendUrl={baseUrl}
        operator={session.user.name}
        workspaceRoot={modeInfo?.workspace_root ?? null}
      />
      <ReportUsageModal
        accountId={reportUsageId}
        accounts={accounts}
        onCancel={() => setReportUsageId(null)}
        onSubmit={onSubmitUsage}
      />
      <ResetModal
        open={resetTyping}
        busy={adminBusy === "reset"}
        onCancel={() => setResetTyping(false)}
        onConfirm={confirmAdminReset}
      />
      <SpawnAgentModal
        open={spawnAgentOpen}
        onCancel={() => setSpawnAgentOpen(false)}
        backendUrl={baseUrl}
        defaultMachine={session.user.machine}
        accounts={accounts}
      />
      <DeleteAgentModal
        agent={pendingDeleteAgent}
        onCancel={() => setPendingDeleteAgent(null)}
        onConfirm={onConfirmDeleteAgent}
      />
      <EditAgentModal
        agent={pendingEditAgent}
        onCancel={() => setPendingEditAgent(null)}
        onConfirm={onConfirmEditAgent}
      />
      <DeleteSlotModal
        slot={pendingDeleteSlot}
        onCancel={() => setPendingDeleteSlot(null)}
        onConfirm={onConfirmDeleteSlot}
      />
      <LogViewerModal scope="slot" target={logViewerSlot} baseUrl={baseUrl} onClose={() => setLogViewerSlot(null)} />
      <LogViewerModal scope="agent" target={logViewerAgent} baseUrl={baseUrl} onClose={() => setLogViewerAgent(null)} />
      <BacklogDetailModal open={backlogOpen} onClose={() => setBacklogOpen(false)} baseUrl={baseUrl} />
    </div>
  );
}

// ─── desktop layout ──────────────────────────────────────────────────────

// Shared wiring threaded into both DesktopLayout + MobileTriage (Plan B).
interface ActivityFeedWiring {
  activity: ActivityEntry[];
  activityFilter: ActivityFilter;
  onActivityFilter: (f: ActivityFilter) => void;
  onLoadOlderActivity: (beforeId: number) => void;
  loadingOlderActivity: boolean;
  activityHasMore: boolean;
}

interface AgentTypesWiring {
  typesAgents: AgentView[];
  typesShowFinished: boolean;
  setTypesShowFinished: (v: boolean) => void;
}

interface DesktopLayoutProps extends ActivityFeedWiring, AgentTypesWiring {
  t: Tweaks;
  state: StateResponse;
  summary: ReturnType<typeof summarise>;
  accounts: AccountView[];
  agents: AgentView[];
  activeRole: AgentRole | null;
  setActiveRole: (r: AgentRole | null) => void;
  agentHistory: ChatMessage[];
  historyLoading: boolean;
  showArchived: boolean;
  setShowArchived: (v: boolean) => void;
  onSendToRole: (role: string, text: string) => Promise<void>;
  onPromoteAgent: (agentId: string, targetRole: AgentRole) => Promise<void>;
  onArchiveAgent: (agentId: string) => Promise<void>;
  onRestoreAgent: (agentId: string) => Promise<void>;
  onRequestDeleteAgent: (a: AgentView) => void;
  onRequestEditAgent: (a: AgentView) => void;
  onShowAgentLog: (a: AgentView) => void;
  onSpawnAgent: () => void;
  expandedId: number | null;
  setExpandedId: (id: number | null) => void;
  onAct: (action: SlotAction, slot: SlotView) => void;
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole) => void;
  onTogglePrerequisite: (name: string, value: boolean) => void;
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  refreshingUsageId: string | null;
  onSpawn: () => void;
  onOpenBacklog: () => void;
  onReloadBacklog: () => void;
  reloadingBacklog: boolean;
  setTweak: <K extends keyof Tweaks>(k: K, v: Tweaks[K]) => void;
}

function DesktopLayout({
  t,
  state,
  summary,
  activity,
  activityFilter,
  onActivityFilter,
  onLoadOlderActivity,
  loadingOlderActivity,
  activityHasMore,
  typesAgents,
  typesShowFinished,
  setTypesShowFinished,
  accounts,
  agents,
  activeRole,
  setActiveRole,
  agentHistory,
  historyLoading,
  showArchived,
  setShowArchived,
  onSendToRole,
  onPromoteAgent,
  onArchiveAgent,
  onRestoreAgent,
  onRequestDeleteAgent,
  onRequestEditAgent,
  onShowAgentLog,
  onSpawnAgent,
  expandedId,
  setExpandedId,
  onAct,
  onAnswer,
  onTogglePrerequisite,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  refreshingUsageId,
  onSpawn,
  onOpenBacklog,
  onReloadBacklog,
  reloadingBacklog,
  setTweak,
}: DesktopLayoutProps) {
  const activityFeedNode = (
    <ActivityFeed
      activity={activity}
      filter={activityFilter}
      onFilterChange={onActivityFilter}
      onLoadOlder={onLoadOlderActivity}
      loadingOlder={loadingOlderActivity}
      hasMore={activityHasMore}
    />
  );
  const agentTypesNode = (
    <AgentTypesPanel agents={typesAgents} showFinished={typesShowFinished} setShowFinished={setTypesShowFinished} />
  );
  if (state.slots.length === 0) {
    return (
      <>
        <div className="top-row">
          <HealthStrip summary={summary} />
          <BacklogSummary
            backlog={state.backlog_summary}
            onOpenDetail={onOpenBacklog}
            onReloadYaml={onReloadBacklog}
            reloading={reloadingBacklog}
          />
        </div>
        <div className="layout">
          <div className="main-col">
            <AgentsPanel
              agents={agents}
              activeRole={activeRole}
              setActiveRole={setActiveRole}
              history={agentHistory}
              historyLoading={historyLoading}
              showArchived={showArchived}
              setShowArchived={setShowArchived}
              onSendToRole={onSendToRole}
              onPromote={onPromoteAgent}
              onArchive={onArchiveAgent}
              onRestore={onRestoreAgent}
              onDelete={onRequestDeleteAgent}
              onEdit={onRequestEditAgent}
              onLog={onShowAgentLog}
              onSpawnAgent={onSpawnAgent}
            />
            {agentTypesNode}
            <Panel
              title="Fleet"
              flush
              tone="fleet"
              right={
                <button className="btn btn-sm" onClick={onSpawn}>
                  <Icon.Bolt /> Spawn worker
                </button>
              }
            >
              <Empty
                icon={<Icon.Bolt />}
                title="No workers booted yet"
                sub={
                  <>
                    Click <b>Spawn worker</b> above for the curl command, or start a Claude Code session and run{" "}
                    <span className="mono">/boot</span> manually.
                  </>
                }
              />
            </Panel>
            <AccountsPanel
              accounts={accounts}
              onClearRateLimit={onClearRateLimit}
              onReportUsage={onReportUsage}
              onRefreshUsage={onRefreshUsage}
              refreshingId={refreshingUsageId}
            />
          </div>
          <div className="rail">
            <div id="blocked">
              <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} />
            </div>
            {t.showPrerequisitesRail && (
              <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
            )}
            {activityFeedNode}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="top-row">
        <HealthStrip summary={summary} />
        <BacklogSummary
          backlog={state.backlog_summary}
          onOpenDetail={onOpenBacklog}
          onReloadYaml={onReloadBacklog}
          reloading={reloadingBacklog}
        />
      </div>
      <KpiRow summary={summary} backlog={state.backlog_summary} />

      <div className="layout" id="fleet">
        <div className="main-col">
          <AgentsPanel
            agents={agents}
            activeRole={activeRole}
            setActiveRole={setActiveRole}
            history={agentHistory}
            historyLoading={historyLoading}
            showArchived={showArchived}
            setShowArchived={setShowArchived}
            onSendToRole={onSendToRole}
            onPromote={onPromoteAgent}
            onArchive={onArchiveAgent}
            onRestore={onRestoreAgent}
            onDelete={onRequestDeleteAgent}
            onEdit={onRequestEditAgent}
            onLog={onShowAgentLog}
            onSpawnAgent={onSpawnAgent}
          />

          {agentTypesNode}

          <Panel
            title="Fleet"
            count={`${state.slots.length} slot${state.slots.length === 1 ? "" : "s"}`}
            tone="fleet"
            right={
              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                <Tabs
                  value={t.slotLayout}
                  onChange={(v) => setTweak("slotLayout", v)}
                  options={[
                    { value: "table", label: "Table" },
                    { value: "cards", label: "Cards" },
                  ]}
                />
                <button className="btn btn-sm" onClick={onSpawn} title="Spawn a new worker session">
                  <Icon.Bolt /> Spawn worker
                </button>
              </div>
            }
            flush
          >
            {t.slotLayout === "table" ? (
              <SlotTable slots={state.slots} onAct={onAct} expandedId={expandedId} setExpandedId={setExpandedId} />
            ) : (
              <SlotCards slots={state.slots} onAct={onAct} />
            )}
          </Panel>

          <AccountsPanel
            accounts={accounts}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            refreshingId={refreshingUsageId}
          />
        </div>

        <div className="rail">
          <div id="blocked">
            <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} />
          </div>
          {t.showPrerequisitesRail && (
            <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
          )}
          {t.showActivityRail && activityFeedNode}
        </div>
      </div>

      {!t.showActivityRail && activityFeedNode}
    </>
  );
}

// ─── mobile triage view ──────────────────────────────────────────────────

interface MobileTriageProps extends ActivityFeedWiring, AgentTypesWiring {
  state: StateResponse;
  summary: ReturnType<typeof summarise>;
  accounts: AccountView[];
  onAnswer: (q: BlockedView, answer: string, fromRole: FromRole) => void;
  onAct: (action: SlotAction, slot: SlotView) => void;
  onTogglePrerequisite: (name: string, value: boolean) => void;
  onClearRateLimit: (accountId: string) => void;
  onReportUsage: (accountId: string) => void;
  onRefreshUsage: (accountId: string) => void;
  refreshingUsageId: string | null;
}

function MobileTriage({
  state,
  summary,
  activity,
  activityFilter,
  onActivityFilter,
  onLoadOlderActivity,
  loadingOlderActivity,
  activityHasMore,
  typesAgents,
  typesShowFinished,
  setTypesShowFinished,
  accounts,
  onAnswer,
  onAct,
  onTogglePrerequisite,
  onClearRateLimit,
  onReportUsage,
  onRefreshUsage,
  refreshingUsageId,
}: MobileTriageProps) {
  const blockedCount = state.blocked_queue.filter((b) => !b.answered_at).length;
  const [tab, setTab] = useState<"alerts" | "fleet" | "agents" | "activity">(blockedCount > 0 ? "alerts" : "fleet");
  const typesOnline = typesAgents.filter((a) => a.online).length;

  return (
    <>
      <HealthStrip summary={summary} />
      <div className="mobile-tabs">
        <button className={tab === "alerts" ? "active" : ""} onClick={() => setTab("alerts")}>
          Triage
          {blockedCount > 0 && <span className="count-pill alert">{blockedCount}</span>}
        </button>
        <button className={tab === "fleet" ? "active" : ""} onClick={() => setTab("fleet")}>
          Fleet
          <span className="count-pill">{state.slots.length}</span>
        </button>
        <button className={tab === "agents" ? "active" : ""} onClick={() => setTab("agents")}>
          Agents
          <span className="count-pill">{typesOnline}</span>
        </button>
        <button className={tab === "activity" ? "active" : ""} onClick={() => setTab("activity")}>
          Activity
        </button>
      </div>

      {tab === "alerts" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <BlockedPanel queue={state.blocked_queue} onAnswer={onAnswer} />
          {summary.counts.stale > 0 && (
            <Panel title="Stale slots" count={summary.counts.stale} flush>
              <div className="slot-cards">
                {state.slots
                  .filter((s) => s.status === "stale")
                  .map((s) => (
                    <div key={s.slot_id} className={`slot-card ${s.status}`}>
                      <div className="row-1">
                        <span className="id mono">#{s.slot_id}</span>
                        <span className="ago-cell stale" style={{ fontSize: 11.5 }}>
                          {fmtAgo(s.last_ping)}
                        </span>
                      </div>
                      <div style={{ fontSize: 12.5, color: "var(--fg-1)" }}>
                        {s.current_task && (
                          <span className="task-id mono" style={{ marginRight: 6 }}>
                            {s.current_task}
                          </span>
                        )}
                        {s.last_msg}
                      </div>
                      <div style={{ display: "flex", gap: 6 }}>
                        <button className="btn btn-sm btn-danger" onClick={() => onAct("reassign", s)}>
                          <Icon.Refresh /> Reassign
                        </button>
                        <button className="btn btn-sm" onClick={() => onAct("message", s)}>
                          <Icon.Send /> Message
                        </button>
                      </div>
                    </div>
                  ))}
              </div>
            </Panel>
          )}
          <PrerequisitesPanel prerequisites={state.prerequisites} onToggle={onTogglePrerequisite} />
        </div>
      )}

      {tab === "fleet" && (
        <>
          <Panel title="Fleet" count={state.slots.length} flush tone="fleet">
            <SlotCards slots={state.slots} onAct={onAct} />
          </Panel>
          <AccountsPanel
            accounts={accounts}
            onClearRateLimit={onClearRateLimit}
            onReportUsage={onReportUsage}
            onRefreshUsage={onRefreshUsage}
            refreshingId={refreshingUsageId}
          />
        </>
      )}

      {tab === "agents" && (
        <AgentTypesPanel agents={typesAgents} showFinished={typesShowFinished} setShowFinished={setTypesShowFinished} />
      )}

      {tab === "activity" && (
        <ActivityFeed
          activity={activity}
          filter={activityFilter}
          onFilterChange={onActivityFilter}
          onLoadOlder={onLoadOlderActivity}
          loadingOlder={loadingOlderActivity}
          hasMore={activityHasMore}
        />
      )}
    </>
  );
}

// ─── modals ──────────────────────────────────────────────────────────────

function ReassignModal({
  slot,
  onCancel,
  onConfirm,
}: {
  slot: SlotView | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  if (!slot) return null;
  return (
    <Modal open onClose={onCancel} title="Reassign task" danger>
      <div className="modal-body">
        Return <b style={{ color: "var(--fg-0)" }}>{slot.current_task}</b> to the queue? The next idle slot will pick it
        up on its <span className="mono">/boot</span> or <span className="mono">/done</span>. Slot{" "}
        <b style={{ color: "var(--fg-0)" }}>#{slot.slot_id}</b> goes back to <b>idle</b>.
        <div className="mono" style={{ fontSize: 12 }}>
          POST /api/slots/{slot.slot_id}/reassign
        </div>
        <div style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Use this when a slot is silent / stuck / wrong-track. Anything the worker had in flight will be lost unless it
          committed first.
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel <span className="kbd">esc</span>
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          onClick={onConfirm}
        >
          Reassign #{slot.slot_id}
        </button>
      </div>
    </Modal>
  );
}

function SnapshotModal({ open, onCancel, onConfirm }: { open: boolean; onCancel: () => void; onConfirm: () => void }) {
  if (!open) return null;
  return (
    <Modal open onClose={onCancel} title="Take a snapshot">
      <div className="modal-body">
        Writes <span className="mono">state.json</span> to disk, uploads to GCS if configured, and creates a git commit.
        Idempotent — produces no commit if nothing changed.
        <div className="mono" style={{ fontSize: 12 }}>
          POST /api/snapshot?reason=manual
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={onConfirm}>
          <Icon.Camera /> Snapshot now
        </button>
      </div>
    </Modal>
  );
}

function MessageModal({
  slot,
  onCancel,
  onSend,
}: {
  slot: SlotView | null;
  onCancel: () => void;
  onSend: (slot: SlotView, text: string) => void;
}) {
  const [text, setText] = useState("");
  if (!slot) return null;
  const suggestions = [
    "wind down after current QG",
    "commit current diff and quit — fresh session will continue",
    "skip and grab next task",
  ];
  return (
    <Modal open onClose={onCancel} title={`Send message → #${slot.slot_id}`}>
      <div className="modal-body">
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginBottom: 8 }}>
          The worker sees this on its next <span className="mono">/progress</span> call (≤5s).
        </div>
        <textarea
          rows={4}
          autoFocus
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="e.g. context pressure high — commit, quit, fresh session"
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {suggestions.map((s) => (
            <button key={s} className="btn btn-xs" onClick={() => setText(s)}>
              {s}
            </button>
          ))}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" disabled={!text.trim()} onClick={() => onSend(slot, text)}>
          <Icon.Send /> Send to #{slot.slot_id}
        </button>
      </div>
    </Modal>
  );
}

function LoopIntervalModal({
  slot,
  onCancel,
  onSet,
}: {
  slot: SlotView | null;
  onCancel: () => void;
  onSet: (slot: SlotView, seconds: number) => void;
}) {
  const [seconds, setSeconds] = useState(60);
  if (!slot) return null;
  const presets = [60, 300, 900, 1800];
  const clamped = clampLoopSeconds(seconds);
  return (
    <Modal open onClose={onCancel} title={`Set /loop cadence → #${slot.slot_id}`}>
      <div className="modal-body">
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginBottom: 8 }}>
          Re-issues the agent&apos;s <span className="mono">/loop</span> at the new cadence (live nudge — no respawn,
          keeps its task + context). Bounded to 30–3600s.
        </div>
        <input
          type="number"
          min={30}
          max={3600}
          autoFocus
          value={seconds}
          onChange={(e) => setSeconds(Number(e.target.value))}
        />
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap", marginTop: 8 }}>
          {presets.map((p) => (
            <button key={p} className="btn btn-xs" onClick={() => setSeconds(p)}>
              {p}s
            </button>
          ))}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => onSet(slot, clamped)}>
          <Icon.Bolt /> Set {clamped}s
        </button>
      </div>
    </Modal>
  );
}

// ─── small helpers ───────────────────────────────────────────────────────

// ─── Spawn worker modal ──────────────────────────────────────────────────

interface SpawnModalProps {
  open: boolean;
  onCancel: () => void;
  slots: SlotView[];
  accounts: AccountView[];
  backendUrl: string;
  operator: string;
  // Worktree base of the backend this modal spawns against (from /api/mode).
  // null until /api/mode resolves. Used to build the per-slot worktree default
  // so the VM UI gets the VM's path, not the operator's laptop path.
  workspaceRoot: string | null;
}

function SpawnModal({ open, onCancel, slots, accounts, backendUrl, operator, workspaceRoot }: SpawnModalProps) {
  // Pick the next free integer slot id (skip occupied ones).
  const occupied = new Set(slots.map((s) => s.slot_id));
  let nextSlot = 2;
  while (occupied.has(nextSlot)) nextSlot++;

  const [slotId, setSlotId] = useState<number>(nextSlot);
  const [accountId, setAccountId] = useState<string>(
    accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]?.account_id ?? "",
  );
  const [model, setModel] = useState<"sonnet" | "opus" | "haiku">("sonnet");
  const [effort, setEffort] = useState<"low" | "medium" | "high" | "xhigh" | "max">("high");
  const [thinkingOn, setThinkingOn] = useState<boolean>(false);
  // Working directory for the spawned claude subprocess. Auto-fills to the
  // per-slot worktree convention (`<workspace_root>/.tabs/<slot_id>/`) so
  // the worker lands in its own git worktree per the PM-repo convention.
  // Empty = "wherever the backend was started from" (only useful for
  // spawning a worker that operates on the orchestrator repo itself).
  // The workspace_root prefix comes from /api/mode for the backend we're
  // pointed at — so the VM UI gets the VM's path, not this laptop's. Stays
  // empty until /api/mode resolves; operator can edit per-spawn.
  const [cwd, setCwd] = useState<string>("");
  const [cwdEdited, setCwdEdited] = useState<boolean>(false);
  useEffect(() => {
    if (!cwdEdited) {
      // Default to the per-tab worktree convention, rooted at the backend's
      // own workspace root (matches server worktree_setup.slot_dir).
      setCwd(workspaceRoot ? `${workspaceRoot}/.tabs/${slotId}/` : "");
    }
  }, [slotId, cwdEdited, workspaceRoot]);

  // Server-rendered prompt (editable in the textarea before spawn). Fetched
  // from /api/spawn/preview whenever the inputs change. agents/worker.md is the
  // source of truth — operator can also click "Reload prompts" to re-read it.
  const [bootPrompt, setBootPrompt] = useState<string>("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState(false);
  const [spawnResult, setSpawnResult] = useState<{ tmux_session: string; attach_hint: string } | null>(null);

  // Reset nextSlot whenever slots list changes.
  useEffect(() => {
    if (open) setSlotId(nextSlot);
  }, [open, nextSlot]);

  // Pre-pick the first available (or first overall) account when the accounts
  // list arrives. useState's initializer only fires once on mount; if accounts
  // hadn't loaded by then the boot prompt would render "<set me>" forever
  // unless the operator manually picked from the dropdown.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      const fallback = accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]!.account_id;
      setAccountId(fallback);
    }
  }, [accounts, accountId]);

  // Refetch the rendered preview whenever inputs change (or modal opens).
  // We don't auto-refetch when the textarea is dirty — operator edits would
  // be silently nuked otherwise.
  const fetchPreview = useCallback(async () => {
    if (!open) return;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await api.spawnPreview(backendUrl, {
        slot_id: slotId,
        server_url: backendUrl,
        account_id: accountId || undefined,
        operator,
        model,
        effort,
        thinking: thinkingOn ? "on" : "off",
      });
      setBootPrompt(res.prompt);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [open, slotId, accountId, operator, model, effort, thinkingOn, backendUrl]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  const reloadFromFile = async () => {
    try {
      await api.reloadPrompts(backendUrl);
      await fetchPreview();
    } catch (e) {
      setPreviewError(`Reload failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const spawnHere = async () => {
    setSpawnBusy(true);
    setPreviewError(null);
    try {
      const res = await api.spawnSlot(backendUrl, slotId, {
        account_id: accountId || undefined,
        boot_prompt: bootPrompt,
        cwd: cwd || undefined,
      });
      setSpawnResult({ tmux_session: res.tmux_session, attach_hint: res.attach_hint });
    } catch (e) {
      setPreviewError(`Spawn failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSpawnBusy(false);
    }
  };

  if (!open) return null;

  const copyToClipboard = (text: string) => {
    void navigator.clipboard?.writeText(text);
  };

  return (
    <Modal open onClose={onCancel} title="Spawn a new worker">
      <div className="modal-body spawn-modal-body">
        <div className="spawn-section">
          <h4>What to spawn</h4>
          <div className="spawn-fields-grid">
            <div className="spawn-field">
              <span>Slot ID</span>
              <input
                type="number"
                min={2}
                value={slotId}
                onChange={(e) => setSlotId(parseInt(e.target.value, 10) || nextSlot)}
              />
            </div>
            <div className="spawn-field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id} disabled={a.status === "rate_limited"}>
                    {a.label} ({a.tier}
                    {a.status === "rate_limited" ? " — rate-limited" : ""})
                  </option>
                ))}
              </select>
            </div>
            <div className="spawn-field">
              <span>Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value as "sonnet" | "opus" | "haiku")}>
                <option value="sonnet">sonnet</option>
                <option value="opus">opus</option>
                <option value="haiku">haiku</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Effort</span>
              <select
                value={effort}
                onChange={(e) => setEffort(e.target.value as typeof effort)}
                title="Maps to `claude --effort <level>`. Higher = more thinking budget, more cost."
              >
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
                <option value="max">max</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Thinking</span>
              <label style={{ display: "inline-flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input type="checkbox" checked={thinkingOn} onChange={(e) => setThinkingOn(e.target.checked)} />
                <span style={{ fontSize: 12, color: "var(--fg-2)" }}>
                  {thinkingOn ? "on" : "off"} — extended thinking
                </span>
              </label>
            </div>
            <div className="spawn-field" style={{ gridColumn: "1 / -1" }}>
              <span>Worktree (cwd)</span>
              <input
                type="text"
                placeholder="leave empty to run in backend cwd (orchestrator/)"
                value={cwd}
                onChange={(e) => {
                  setCwd(e.target.value);
                  setCwdEdited(true);
                }}
              />
            </div>
          </div>
        </div>

        <div className="spawn-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h4 style={{ margin: 0 }}>Boot prompt</h4>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => void reloadFromFile()}
              title="Re-read agents/worker.md from disk + refresh the textarea below"
            >
              ↻ Reload prompts
            </button>
          </div>
          <p style={{ marginTop: 0, marginBottom: 8, color: "var(--fg-2)", fontSize: 12 }}>
            Rendered from <span className="mono">agents/worker.md</span> server-side. Edit inline for one-off tweaks —
            the server pastes whatever's in this box. For a sticky change, edit the file and click <b>Reload prompts</b>
            .
          </p>
          <textarea
            className="spawn-cmd"
            style={{ width: "100%", minHeight: 260, fontFamily: "var(--font-mono)", fontSize: 11 }}
            value={previewLoading && !bootPrompt ? "Loading…" : bootPrompt}
            onChange={(e) => setBootPrompt(e.target.value)}
            spellCheck={false}
          />
          {previewError && (
            <div style={{ color: "var(--status-stale, #b91c1c)", fontSize: 12, marginTop: 6 }}>{previewError}</div>
          )}
          {spawnResult && (
            <div
              style={{
                background: "var(--bg-3)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                marginTop: 8,
              }}
            >
              ✓ Spawned tmux session <span className="mono">{spawnResult.tmux_session}</span>. Attach with:{" "}
              <span className="mono">{spawnResult.attach_hint}</span>{" "}
              <button
                className="btn btn-xs"
                onClick={() => copyToClipboard(spawnResult.attach_hint)}
                title="Copy attach command"
              >
                Copy
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Close
        </button>
        <button
          className="btn btn-sm"
          onClick={() => copyToClipboard(bootPrompt)}
          disabled={!bootPrompt}
          title="Copy the prompt to paste into a Claude Code session manually"
        >
          Copy prompt
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => void spawnHere()}
          disabled={spawnBusy || !bootPrompt}
          title="Spawn an interactive Claude session in a tmux pane on this machine and paste the prompt"
        >
          {spawnBusy ? "Spawning…" : "Spawn here →"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Report usage modal ──────────────────────────────────────────────────

interface ReportUsageModalProps {
  accountId: string | null;
  accounts: AccountView[];
  onCancel: () => void;
  onSubmit: (accountId: string, weekly: number | null, fiveHour: number | null) => Promise<void>;
}

function ReportUsageModal({ accountId, accounts, onCancel, onSubmit }: ReportUsageModalProps) {
  const account = accounts.find((a) => a.account_id === accountId);
  const [weekly, setWeekly] = useState<string>("");
  const [fiveHour, setFiveHour] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (account) {
      setWeekly(String(account.weekly_msgs_used));
      setFiveHour(String(account.five_hour_msgs_used));
    }
  }, [account]);

  if (!accountId || !account) return null;

  const submit = async () => {
    setSubmitting(true);
    try {
      await onSubmit(
        accountId,
        weekly.trim() === "" ? null : parseInt(weekly, 10),
        fiveHour.trim() === "" ? null : parseInt(fiveHour, 10),
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open onClose={onCancel} title={`Report usage — ${account.label}`}>
      <div className="modal-body">
        <p style={{ marginTop: 0, fontSize: 12, color: "var(--fg-2)" }}>
          Enter the current numbers from <span className="mono">/usage</span> in any Claude Code session running this
          account. Leaves blank values unchanged.
        </p>
        <div className="spawn-field">
          <span>Weekly used</span>
          <input
            type="number"
            min={0}
            max={account.weekly_msg_limit}
            value={weekly}
            onChange={(e) => setWeekly(e.target.value)}
          />
        </div>
        <div className="spawn-field">
          <span>5-hour used</span>
          <input type="number" min={0} value={fiveHour} onChange={(e) => setFiveHour(e.target.value)} />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" disabled={submitting} onClick={submit}>
          <Icon.Check /> {submitting ? "Reporting…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Backlog detail modal ────────────────────────────────────────────────

interface BacklogDetailModalProps {
  open: boolean;
  onClose: () => void;
  baseUrl: string;
}

type BacklogFilter = "all" | "queued" | "dispatched" | "done" | "cancelled";

function BacklogDetailModal({ open, onClose, baseUrl }: BacklogDetailModalProps) {
  const [tasks, setTasks] = useState<BacklogTaskView[] | null>(null);
  const [filter, setFilter] = useState<BacklogFilter>("queued");
  const [loading, setLoading] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [blockers, setBlockers] = useState<Record<string, string>>({});

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.backlog(baseUrl);
      setTasks(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => {
    if (open) {
      load();
      setExpandedId(null);
      setBlockers({});
    }
  }, [open, load]);

  const reload = async () => {
    setReloading(true);
    try {
      await api.reloadBacklog(baseUrl);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setReloading(false);
    }
  };

  const fetchBlockers = async (taskId: string) => {
    if (blockers[taskId]) return;
    try {
      const r = await api.taskBlockers(baseUrl, taskId);
      setBlockers((prev) => ({ ...prev, [taskId]: r.explanation }));
    } catch (e) {
      setBlockers((prev) => ({ ...prev, [taskId]: e instanceof Error ? e.message : String(e) }));
    }
  };

  const filtered = useMemo(() => {
    if (!tasks) return [];
    return filter === "all" ? tasks : tasks.filter((t) => t.status === filter);
  }, [tasks, filter]);

  const counts = useMemo(() => {
    const c = { all: 0, queued: 0, dispatched: 0, done: 0, cancelled: 0 };
    (tasks ?? []).forEach((t) => {
      c.all++;
      if (t.status === "queued") c.queued++;
      else if (t.status === "dispatched") c.dispatched++;
      else if (t.status === "done") c.done++;
      else if (t.status === "cancelled") c.cancelled++;
    });
    return c;
  }, [tasks]);

  if (!open) return null;

  return (
    <Modal open onClose={onClose} title="Backlog detail">
      <div className="modal-body" style={{ minWidth: "min(900px, 90vw)", maxHeight: "70vh", overflow: "auto" }}>
        <div style={{ display: "flex", gap: 6, alignItems: "center", marginBottom: 12, flexWrap: "wrap" }}>
          {(["queued", "dispatched", "done", "cancelled", "all"] as BacklogFilter[]).map((f) => (
            <button key={f} className={`btn btn-xs ${filter === f ? "btn-primary" : ""}`} onClick={() => setFilter(f)}>
              {f}{" "}
              <span className="mono" style={{ marginLeft: 4, color: "var(--fg-3)" }}>
                {counts[f]}
              </span>
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <button className="btn btn-xs" onClick={load} disabled={loading} title="Re-fetch from server">
            <Icon.Refresh /> {loading ? "Loading…" : "Refresh"}
          </button>
          <button
            className="btn btn-xs"
            onClick={reload}
            disabled={reloading}
            title="Re-read backlog.yaml from disk on the server"
          >
            <Icon.Bolt /> {reloading ? "Reloading…" : "Reload YAML"}
          </button>
        </div>

        {error && <div style={{ color: "var(--status-stale)", marginBottom: 10, fontSize: 12 }}>{error}</div>}
        {!loading && filtered.length === 0 && (
          <div style={{ color: "var(--fg-2)", fontSize: 13, padding: "20px 0" }}>
            No tasks in <b>{filter}</b>.
          </div>
        )}

        <table className="backlog-table" style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
          <thead>
            <tr style={{ textAlign: "left", color: "var(--fg-2)", borderBottom: "1px solid var(--border)" }}>
              <th style={{ padding: "6px 4px" }}>ID</th>
              <th style={{ padding: "6px 4px" }}>Title</th>
              <th style={{ padding: "6px 4px" }}>Tier · Pri</th>
              <th style={{ padding: "6px 4px" }}>Status</th>
              <th style={{ padding: "6px 4px" }}>Slot</th>
              <th style={{ padding: "6px 4px" }}>Est</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((t) => {
              const isOpen = expandedId === t.id;
              return (
                <Fragment key={t.id}>
                  <tr
                    onClick={() => {
                      const next = isOpen ? null : t.id;
                      setExpandedId(next);
                      if (next && (t.status === "queued" || t.status === "dispatched")) {
                        fetchBlockers(t.id);
                      }
                    }}
                    style={{
                      cursor: "pointer",
                      borderBottom: "1px solid var(--border)",
                      background: isOpen ? "var(--bg-2)" : "transparent",
                    }}
                  >
                    <td className="mono" style={{ padding: "6px 4px" }}>
                      {t.id}
                    </td>
                    <td style={{ padding: "6px 4px" }}>{t.title}</td>
                    <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                      T{t.tier}·P{t.priority}
                    </td>
                    <td style={{ padding: "6px 4px" }}>
                      <span
                        className="mono"
                        style={{
                          fontSize: 10.5,
                          textTransform: "uppercase",
                          letterSpacing: "0.04em",
                          padding: "2px 5px",
                          borderRadius: 3,
                          color:
                            t.status === "dispatched"
                              ? "var(--status-working)"
                              : t.status === "done"
                                ? "var(--fg-2)"
                                : t.status === "cancelled"
                                  ? "var(--status-stale)"
                                  : t.status === "skipped"
                                    ? "var(--status-paused)"
                                    : "var(--fg-1)",
                          background:
                            t.status === "dispatched"
                              ? "var(--status-working-bg)"
                              : t.status === "done"
                                ? "transparent"
                                : t.status === "cancelled"
                                  ? "var(--status-stale-bg)"
                                  : t.status === "skipped"
                                    ? "var(--status-paused-bg)"
                                    : "var(--bg-1)",
                        }}
                      >
                        {t.status}
                      </span>
                    </td>
                    <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                      {t.dispatched_to != null
                        ? `#${t.dispatched_to}`
                        : t.done_sha
                          ? t.done_sha.slice(0, 7)
                          : t.target_slot != null && t.affinity !== "none"
                            ? (() => {
                                const affColor =
                                  t.affinity === "high"
                                    ? "var(--status-blocked)"
                                    : t.affinity === "medium"
                                      ? "var(--status-paused)"
                                      : "var(--fg-2)";
                                return (
                                  <span style={{ color: affColor }} title={`affinity=${t.affinity}`}>
                                    →#{t.target_slot} <span style={{ fontSize: 10 }}>({t.affinity})</span>
                                  </span>
                                );
                              })()
                            : "—"}
                    </td>
                    <td className="mono" style={{ padding: "6px 4px", color: "var(--fg-2)" }}>
                      {t.est_hours}h
                    </td>
                  </tr>
                  {isOpen && (
                    <tr style={{ background: "var(--bg-2)" }}>
                      <td
                        colSpan={6}
                        style={{ padding: "10px 12px 14px 12px", borderBottom: "1px solid var(--border)" }}
                      >
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
                          <div>
                            <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>BRIEF</div>
                            <pre
                              style={{
                                margin: 0,
                                fontFamily: "var(--font-mono)",
                                fontSize: 12,
                                whiteSpace: "pre-wrap",
                                color: "var(--fg-1)",
                              }}
                            >
                              {t.brief || "(no brief)"}
                            </pre>
                          </div>
                          <div>
                            <div style={{ fontSize: 11, color: "var(--fg-3)", marginBottom: 4 }}>DONE WHEN</div>
                            <pre
                              style={{
                                margin: 0,
                                fontFamily: "var(--font-mono)",
                                fontSize: 12,
                                whiteSpace: "pre-wrap",
                                color: "var(--fg-1)",
                              }}
                            >
                              {t.done_definition || "(none)"}
                            </pre>
                          </div>
                        </div>
                        <div style={{ marginTop: 10, fontSize: 11.5, color: "var(--fg-2)" }}>
                          <span className="mono">plan_ref:</span> {t.plan_ref || "—"}
                          {t.repos.length > 0 && (
                            <>
                              {" "}
                              · <span className="mono">repos:</span> {t.repos.join(", ")}
                            </>
                          )}
                          {t.collision_group && (
                            <>
                              {" "}
                              · <span className="mono">group:</span> {t.collision_group}
                            </>
                          )}
                        </div>
                        {(t.status === "queued" || t.status === "dispatched") && (
                          <div style={{ marginTop: 8, fontSize: 11.5, color: "var(--fg-2)" }}>
                            <span className="mono">why this rank:</span>{" "}
                            {blockers[t.id] ?? <span style={{ color: "var(--fg-3)" }}>loading…</span>}
                          </div>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

// ─── Spawn agent modal ───────────────────────────────────────────────────

interface SpawnAgentModalProps {
  open: boolean;
  onCancel: () => void;
  backendUrl: string;
  defaultMachine: string | null;
  accounts: AccountView[];
}

function SpawnAgentModal({ open, onCancel, backendUrl, defaultMachine, accounts }: SpawnAgentModalProps) {
  const [role, setRole] = useState<AgentRole>("main");
  const [label, setLabel] = useState("");
  const [machine, setMachine] = useState(defaultMachine ?? "");
  // Phase 4b-cleanup made account_id mandatory on the backend (every spawned
  // claude session must have a setup-token env file). Default to the first
  // available account; operator can override via the dropdown.
  const [accountId, setAccountId] = useState<string>(
    accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]?.account_id ?? "",
  );
  const [model, setModel] = useState<string>("opus");
  const [effort, setEffort] = useState<string>("high");
  const [thinking, setThinking] = useState<boolean>(true);
  const [pollIntervalSeconds, setPollIntervalSeconds] = useState<number | null>(null);
  const [bootPrompt, setBootPrompt] = useState("");
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [spawnBusy, setSpawnBusy] = useState(false);
  const [spawnResult, setSpawnResult] = useState<{
    agent_id: string;
    tmux_session: string;
    attach_hint: string;
    rc_url: string | null;
    rc_url_warning: string | null;
  } | null>(null);

  useEffect(() => {
    setMachine(defaultMachine ?? "");
  }, [defaultMachine]);

  // Pre-pick an account when the accounts list lands after first render.
  // Mirrors SpawnModal's behaviour so the boot prompt renders with a real
  // account_id instead of "<set me>" and the Spawn click doesn't 400.
  useEffect(() => {
    if (!accountId && accounts.length > 0) {
      const fallback = accounts.find((a) => a.status === "healthy")?.account_id ?? accounts[0]!.account_id;
      setAccountId(fallback);
    }
  }, [accounts, accountId]);

  // Default model follows role convention: opus for main, sonnet for review/custom.
  // Thinking defaults to ON for main/review (decision-heavy) and custom — matches
  // Anthropic's tier-selection guidance.
  useEffect(() => {
    if (role === "main") {
      setModel("opus");
      setEffort("high");
      setThinking(true);
    } else if (role === "review") {
      setModel("sonnet");
      setEffort("high");
      setThinking(true);
    } else {
      setModel("sonnet");
      setEffort("high");
      setThinking(true);
    }
  }, [role]);

  const defaultLabel = label || `${role.charAt(0).toUpperCase()}${role.slice(1)} agent`;

  const fetchPreview = useCallback(async () => {
    if (!open) return;
    // custom doesn't map to a template file; reuse main as a starting point.
    const previewRole = role === "custom" ? "main" : role;
    setPreviewLoading(true);
    setPreviewError(null);
    try {
      const res = await api.spawnAgentPreview(backendUrl, {
        role: previewRole,
        server_url: backendUrl,
        machine: machine || undefined,
        model,
        effort,
        thinking: thinking ? "on" : "off",
      });
      setBootPrompt(res.prompt);
    } catch (e) {
      setPreviewError(e instanceof Error ? e.message : String(e));
    } finally {
      setPreviewLoading(false);
    }
  }, [open, role, machine, backendUrl, model, effort, thinking]);

  useEffect(() => {
    void fetchPreview();
  }, [fetchPreview]);

  const reloadFromFile = async () => {
    try {
      await api.reloadPrompts(backendUrl);
      await fetchPreview();
    } catch (e) {
      setPreviewError(`Reload failed: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const copy = () => {
    void navigator.clipboard?.writeText(bootPrompt);
  };

  const spawnHere = async () => {
    if (!accountId) {
      setPreviewError("Pick an account before spawning — every claude session needs a setup-token env file.");
      return;
    }
    setSpawnBusy(true);
    setPreviewError(null);
    try {
      const res = await api.spawnAgent(backendUrl, {
        role: role === "custom" ? "main" : role,
        label: defaultLabel,
        account_id: accountId,
        machine: machine || undefined,
        model,
        effort,
        thinking,
        poll_interval_seconds: pollIntervalSeconds ?? undefined,
        boot_prompt: bootPrompt,
      });
      setSpawnResult({
        agent_id: res.agent_id,
        tmux_session: res.tmux_session,
        attach_hint: res.attach_hint,
        rc_url: res.rc_url,
        rc_url_warning: res.rc_url_warning,
      });
    } catch (e) {
      setPreviewError(`Spawn failed: ${e instanceof Error ? e.message : String(e)}`);
    } finally {
      setSpawnBusy(false);
    }
  };

  if (!open) return null;

  return (
    <Modal open onClose={onCancel} title="Spawn agent">
      <div className="modal-body spawn-modal-body">
        <div className="spawn-section">
          <h4>What to spawn</h4>
          <div className="spawn-fields-grid">
            <div className="spawn-field">
              <span>Role</span>
              <select value={role} onChange={(e) => setRole(e.target.value as AgentRole)}>
                <option value="main">main — orchestration + chat</option>
                <option value="review">review — quality checks + chat</option>
                <option value="custom">custom — operator-defined (uses main.md)</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Label</span>
              <input type="text" placeholder={defaultLabel} value={label} onChange={(e) => setLabel(e.target.value)} />
            </div>
            <div className="spawn-field">
              <span>Account</span>
              <select value={accountId} onChange={(e) => setAccountId(e.target.value)}>
                {accounts.length === 0 && <option value="">(no accounts loaded)</option>}
                {accounts.map((a) => (
                  <option key={a.account_id} value={a.account_id} disabled={a.status === "rate_limited"}>
                    {a.label} ({a.tier}
                    {a.status === "rate_limited" ? " — rate-limited" : ""})
                  </option>
                ))}
              </select>
            </div>
            <div className="spawn-field">
              <span>Machine</span>
              <input
                type="text"
                placeholder="hostname where the Claude session runs"
                value={machine}
                onChange={(e) => setMachine(e.target.value)}
              />
            </div>
            <div className="spawn-field">
              <span>Model</span>
              <select value={model} onChange={(e) => setModel(e.target.value)}>
                <option value="opus">opus</option>
                <option value="sonnet">sonnet</option>
                <option value="haiku">haiku</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Effort</span>
              <select value={effort} onChange={(e) => setEffort(e.target.value)}>
                <option value="low">low</option>
                <option value="medium">medium</option>
                <option value="high">high</option>
                <option value="xhigh">xhigh</option>
                <option value="max">max</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Thinking</span>
              <select value={thinking ? "on" : "off"} onChange={(e) => setThinking(e.target.value === "on")}>
                <option value="off">off</option>
                <option value="on">on (max thinking tokens)</option>
              </select>
            </div>
            <div className="spawn-field">
              <span>Poll interval (s)</span>
              <input
                type="number"
                min={1}
                max={3600}
                placeholder="template default"
                value={pollIntervalSeconds ?? ""}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setPollIntervalSeconds(v === "" ? null : Math.max(1, Math.min(3600, parseInt(v, 10) || 60)));
                }}
                title="Override the /loop poll cadence in the boot prompt. Leave blank to use the template's default (60s for main, 15 min for review)."
              />
            </div>
          </div>
          <p style={{ marginTop: 8, marginBottom: 0, color: "var(--fg-2)", fontSize: 12 }}>
            <b>Spawn here</b> runs <span className="mono">claude</span> in a tmux pane, pastes the boot prompt, and
            auto-captures the agent's <span className="mono">/remote-control</span> URL so the dashboard's{" "}
            <b>↗ claude.ai/code</b> link works from mobile without any extra steps. <b>Copy prompt</b> is the manual
            path: paste into a Claude session yourself, then attach the RC URL via the agent's <b>Edit</b> button.
          </p>
        </div>

        <div className="spawn-section">
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
            <h4 style={{ margin: 0 }}>Boot prompt</h4>
            <button
              className="btn btn-xs btn-ghost"
              onClick={() => void reloadFromFile()}
              title={`Re-read agents/${role === "custom" ? "main" : role}.md from disk + refresh the textarea below`}
            >
              ↻ Reload prompts
            </button>
          </div>
          <p style={{ marginTop: 0, marginBottom: 8, color: "var(--fg-2)", fontSize: 12 }}>
            Rendered from <span className="mono">agents/{role === "custom" ? "main" : role}.md</span> server-side. Edit
            inline for one-off tweaks. For a sticky change, edit the file and click <b>Reload prompts</b>.
          </p>
          <textarea
            className="spawn-cmd"
            style={{ width: "100%", minHeight: 220, fontFamily: "var(--font-mono)", fontSize: 11 }}
            value={previewLoading && !bootPrompt ? "Loading…" : bootPrompt}
            onChange={(e) => setBootPrompt(e.target.value)}
            spellCheck={false}
          />
          {previewError && (
            <div style={{ color: "var(--status-stale, #b91c1c)", fontSize: 12, marginTop: 6 }}>{previewError}</div>
          )}
          {spawnResult && (
            <div
              style={{
                background: "var(--bg-3)",
                padding: "8px 10px",
                borderRadius: 6,
                fontSize: 12,
                marginTop: 8,
                lineHeight: 1.5,
              }}
            >
              ✓ Spawned agent <span className="mono">{spawnResult.agent_id}</span> in tmux session{" "}
              <span className="mono">{spawnResult.tmux_session}</span>.
              <br />
              {spawnResult.rc_url ? (
                <>
                  ✓ RC URL auto-captured: <span className="mono">{spawnResult.rc_url}</span>
                </>
              ) : (
                <span style={{ color: "var(--status-stale, #b91c1c)" }}>
                  ⚠{" "}
                  {spawnResult.rc_url_warning ??
                    "RC URL capture failed — attach + run /remote-control manually, then paste via Edit."}
                </span>
              )}
              <br />
              Attach: <span className="mono">{spawnResult.attach_hint}</span>{" "}
              <button className="btn btn-xs" onClick={() => copy()} title="Copy attach command">
                Copy
              </button>
            </div>
          )}
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel}>
          Close
        </button>
        <button className="btn btn-sm" onClick={copy} disabled={!bootPrompt}>
          Copy prompt
        </button>
        <button
          className="btn btn-sm btn-primary"
          onClick={() => void spawnHere()}
          disabled={spawnBusy || !bootPrompt}
          title="Spawn an interactive Claude session in tmux + auto-register + auto-capture RC URL"
        >
          {spawnBusy ? "Spawning…" : "Spawn here →"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Log viewer modal (tmux capture-pane) ────────────────────────────────

function LogViewerModal({
  scope,
  target,
  baseUrl,
  onClose,
}: {
  scope: "slot" | "agent";
  target: SlotView | AgentView | null;
  baseUrl: string;
  onClose: () => void;
}) {
  const [text, setText] = useState<string>("");
  const [available, setAvailable] = useState<boolean>(true);
  const [tmuxSession, setTmuxSession] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
  const preRef = React.useRef<HTMLPreElement>(null);

  const targetLabel = target
    ? scope === "slot"
      ? `#${(target as SlotView).slot_id}`
      : `${(target as AgentView).agent_id}`
    : "";

  const refresh = useCallback(async () => {
    if (!target) return;
    setLoading(true);
    setError(null);
    try {
      const res =
        scope === "slot"
          ? await api.slotLog(baseUrl, (target as SlotView).slot_id, 10000)
          : await api.agentLog(baseUrl, (target as AgentView).agent_id, 10000);
      setText(res.lines);
      setAvailable(res.available);
      setTmuxSession(res.tmux_session);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [target, scope, baseUrl]);

  // Initial load + auto-refresh ticker (5s) while modal is open.
  useEffect(() => {
    if (!target) return;
    void refresh();
    if (!autoRefresh) return;
    const id = setInterval(refresh, 5000);
    return () => clearInterval(id);
  }, [target, refresh, autoRefresh]);

  // Auto-scroll the pre to the bottom when new content arrives.
  useEffect(() => {
    if (preRef.current) preRef.current.scrollTop = preRef.current.scrollHeight;
  }, [text]);

  if (!target) return null;

  return (
    <Modal open onClose={onClose} title={`Log — ${targetLabel}`}>
      <div className="modal-body" style={{ minWidth: 720, maxWidth: "92vw" }}>
        <div
          style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, color: "var(--fg-2)", fontSize: 12 }}
        >
          {tmuxSession ? (
            <>
              <span>
                tmux <span className="mono">{tmuxSession}</span> · last 10000 lines
              </span>
              <span style={{ flex: 1 }} />
              <label style={{ display: "inline-flex", alignItems: "center", gap: 4, cursor: "pointer" }}>
                <input type="checkbox" checked={autoRefresh} onChange={(e) => setAutoRefresh(e.target.checked)} />
                <span>Auto-refresh (5s)</span>
              </label>
              <button className="btn btn-xs" onClick={() => void refresh()} disabled={loading}>
                {loading ? "…" : "Refresh"}
              </button>
            </>
          ) : (
            <span style={{ color: "var(--status-stale)" }}>
              No tmux session attached — this {scope} was started by manual copy/paste, not Spawn here. Use{" "}
              <span className="mono">tmux attach</span> in a terminal if you have a session running outside the
              dashboard.
            </span>
          )}
        </div>
        {error && <div style={{ color: "var(--status-stale)", fontSize: 12, marginBottom: 6 }}>{error}</div>}
        {available && (
          <pre
            ref={preRef}
            style={{
              background: "var(--bg-3)",
              color: "var(--fg-1)",
              padding: 10,
              borderRadius: 4,
              fontFamily: "var(--font-mono)",
              fontSize: 11,
              lineHeight: 1.4,
              minHeight: 360,
              maxHeight: "60vh",
              overflow: "auto",
              whiteSpace: "pre",
              margin: 0,
            }}
          >
            {text || (loading ? "Loading…" : "(empty)")}
          </pre>
        )}
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>
    </Modal>
  );
}

// ─── Delete slot confirmation modal ──────────────────────────────────────

function DeleteSlotModal({
  slot,
  onCancel,
  onConfirm,
}: {
  slot: SlotView | null;
  onCancel: () => void;
  onConfirm: (slotId: number) => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!slot) setTyped("");
  }, [slot]);
  if (!slot) return null;
  const blocked = slot.current_task != null;
  return (
    <Modal open onClose={onCancel} title={`Delete slot #${slot.slot_id}`} danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 8px 0" }}>
          Removes slot <b style={{ color: "var(--fg-0)" }}>#{slot.slot_id}</b> from the dashboard. If the slot has a
          tmux session (<span className="mono">{slot.tmux_session || "—"}</span>), it will also be killed.
        </p>
        {blocked && (
          <p style={{ color: "var(--status-stale)", fontSize: 12 }}>
            ⚠ Slot has current task <span className="mono">{slot.current_task}</span> — reassign first, then delete.
          </p>
        )}
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>delete</b>
          </span>{" "}
          to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="delete"
          autoFocus
          disabled={busy || blocked}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={busy || blocked || typed !== "delete"}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(slot.slot_id);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete slot"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Edit agent metadata modal ───────────────────────────────────────────

function EditAgentModal({
  agent,
  onCancel,
  onConfirm,
}: {
  agent: AgentView | null;
  onCancel: () => void;
  onConfirm: (
    agentId: string,
    fields: { label?: string; rc_url?: string; machine?: string; model?: string },
  ) => Promise<void>;
}) {
  const [label, setLabel] = useState("");
  const [rcUrl, setRcUrl] = useState("");
  const [machine, setMachine] = useState("");
  const [model, setModel] = useState("");
  const [busy, setBusy] = useState(false);
  // Reseed from the agent when the modal opens (or the target agent changes).
  useEffect(() => {
    if (!agent) return;
    setLabel(agent.label ?? "");
    setRcUrl(agent.rc_url ?? "");
    setMachine(agent.machine ?? "");
    setModel(agent.model ?? "");
  }, [agent]);
  if (!agent) return null;
  const submit = async () => {
    setBusy(true);
    try {
      await onConfirm(agent.agent_id, {
        label: label !== agent.label ? label : undefined,
        rc_url: rcUrl !== (agent.rc_url ?? "") ? rcUrl : undefined,
        machine: machine !== (agent.machine ?? "") ? machine : undefined,
        model: model !== (agent.model ?? "") ? model : undefined,
      });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onCancel} title={`Edit agent ${agent.agent_id}`}>
      <div className="modal-body">
        <p style={{ margin: "0 0 12px 0", color: "var(--fg-2)", fontSize: 12 }}>
          Update the agent metadata. The most common use is pasting the RC URL after running{" "}
          <span className="mono">/remote-control</span> in the agent's Claude session — once set, the dashboard's{" "}
          <b>↗ claude.ai/code</b> link opens that session directly (useful from mobile).
        </p>
        <div className="spawn-field">
          <span>Label</span>
          <input type="text" value={label} onChange={(e) => setLabel(e.target.value)} disabled={busy} />
        </div>
        <div className="spawn-field">
          <span>RC URL</span>
          <input
            type="text"
            placeholder="https://claude.ai/code/session_..."
            value={rcUrl}
            onChange={(e) => setRcUrl(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="spawn-field">
          <span>Machine</span>
          <input
            type="text"
            placeholder="laptop hostname"
            value={machine}
            onChange={(e) => setMachine(e.target.value)}
            disabled={busy}
          />
        </div>
        <div className="spawn-field">
          <span>Model</span>
          <input
            type="text"
            placeholder="opus / sonnet / haiku"
            value={model}
            onChange={(e) => setModel(e.target.value)}
            disabled={busy}
          />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button className="btn btn-sm btn-primary" onClick={() => void submit()} disabled={busy}>
          {busy ? "Saving…" : "Save"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Delete agent confirmation modal ─────────────────────────────────────

function DeleteAgentModal({
  agent,
  onCancel,
  onConfirm,
}: {
  agent: AgentView | null;
  onCancel: () => void;
  onConfirm: (agentId: string) => Promise<void>;
}) {
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!agent) setTyped("");
  }, [agent]);
  if (!agent) return null;
  return (
    <Modal open onClose={onCancel} title={`Delete agent ${agent.agent_id}`} danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 8px 0" }}>
          Removes <b style={{ color: "var(--fg-0)" }}>{agent.label}</b> ({agent.agent_id}) from the dashboard.
          Conversation history for role <b>{agent.role}</b> is preserved (messages are keyed by role, not by agent).
        </p>
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>delete</b>
          </span>{" "}
          to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="delete"
          autoFocus
          disabled={busy}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          disabled={busy || typed !== "delete"}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm(agent.agent_id);
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Deleting…" : "Delete agent"}
        </button>
      </div>
    </Modal>
  );
}

// ─── Reset confirmation modal ────────────────────────────────────────────

function ResetModal({
  open,
  busy,
  onCancel,
  onConfirm,
}: {
  open: boolean;
  busy: boolean;
  onCancel: () => void;
  onConfirm: (typed: string) => void;
}) {
  const [typed, setTyped] = useState("");
  useEffect(() => {
    if (!open) setTyped("");
  }, [open]);
  if (!open) return null;
  return (
    <Modal open onClose={onCancel} title="Reset all state" danger>
      <div className="modal-body">
        <p style={{ margin: "0 0 10px 0" }}>
          Drops all SQLite tables (slots, tasks, blocked queue, prerequisites, accounts usage, compactions, activity
          log) and re-loads from <span className="mono">backlog.yaml</span>
          {" + "}
          <span className="mono">accounts.json</span>. Workers currently running will lose their assignments on next
          /boot.
        </p>
        <p style={{ color: "var(--fg-2)", fontSize: 12 }}>
          Type{" "}
          <span className="mono">
            <b>reset</b>
          </span>{" "}
          below to confirm.
        </p>
        <input
          type="text"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          placeholder="reset"
          autoFocus
          disabled={busy}
        />
      </div>
      <div className="modal-foot">
        <button className="btn btn-sm" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button
          className="btn btn-sm btn-primary"
          style={{ background: "var(--status-stale)", borderColor: "var(--status-stale)" }}
          onClick={() => onConfirm(typed)}
          disabled={busy || typed !== "reset"}
        >
          {busy ? "Resetting…" : "Reset everything"}
        </button>
      </div>
    </Modal>
  );
}

function ErrorBanner({ backend, message, onRetry }: { backend: BackendConfig; message: string; onRetry: () => void }) {
  return (
    <div className="error-banner">
      <span className="icon">
        <Icon.Warn />
      </span>
      <div style={{ flex: 1 }}>
        <div className="title">
          Cannot reach <span style={{ color: "var(--status-stale)" }}>{backend.label}</span>
        </div>
        <div className="url">
          {backend.url} · {message}
        </div>
        <div style={{ color: "var(--fg-2)", fontSize: 12, marginTop: 6, lineHeight: 1.45 }}>
          Dashboard will keep retrying every {POLL_INTERVAL_LABEL}. Last known state shown below — may be stale.
        </div>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button className="btn btn-sm" onClick={onRetry}>
          <Icon.Refresh /> Retry
        </button>
      </div>
    </div>
  );
}

function LoadingState() {
  return (
    <Panel title="Loading…" flush>
      <Empty title="Fetching dashboard state" sub="If this hangs, the backend may be down — check the LivePill." />
    </Panel>
  );
}

function setErrorMsg(setter: (msg: string) => void) {
  return (e: unknown) => setter(e instanceof Error ? e.message : String(e));
}

// Re-export for the App.tsx so we don't need a separate file.
export type { AuthedUser };
